import type {
  CapacityFigures,
  DeliverableInclusion,
  ProjectCapacityPlan,
  ProjectSold,
} from './types';

/**
 * Planned capacity vs target per company category (owner 2026-10-27). One copy of
 * the wording for Projects, Project Capacity, the Sales Tracker and Client
 * Deliverables. Sold = distinct companies with an accepted quote or a live
 * invoice line on the project; over capacity is allowed (never a refusal).
 */

export const CAPACITY_CATEGORIES = [
  { key: 'institution', label: 'Institutions' },
  { key: 'partner', label: 'Partners' },
] as const;

export type CapacityCategory = (typeof CAPACITY_CATEGORIES)[number]['key'];

export const CAPACITY_FIELD_LABELS = {
  institutionCapacity: 'Institution planned capacity',
  institutionTarget: 'Institution target',
  partnerCapacity: 'Partner planned capacity',
  partnerTarget: 'Partner target',
} as const;

const NUMBER = new Intl.NumberFormat('en-US');

function count(n: number): string {
  return NUMBER.format(n);
}

/** "12 of 30 · target 20", "12 · target 20", "12 of 30", or "12". */
export function soldAgainstPlan(sold: number, figures: CapacityFigures): string {
  const base = figures.capacity === null ? count(sold) : `${count(sold)} of ${count(figures.capacity)}`;
  return figures.target === null ? base : `${base} · target ${count(figures.target)}`;
}

/** "Institutions 12 of 30 · target 20". */
export function categoryLine(category: CapacityCategory, sold: ProjectSold, plan: ProjectCapacityPlan): string {
  const label = CAPACITY_CATEGORIES.find((c) => c.key === category)!.label;
  return `${label} ${soldAgainstPlan(sold[category], plan[category])}`;
}

export type CapacityState = 'none' | 'below_target' | 'target_met' | 'over_capacity';

/** Over capacity wins over target met; no target and no capacity = 'none'. */
export function capacityState(sold: number, figures: CapacityFigures): CapacityState {
  if (figures.capacity !== null && sold > figures.capacity) return 'over_capacity';
  if (figures.target !== null) return sold >= figures.target ? 'target_met' : 'below_target';
  return 'none';
}

export const CAPACITY_STATE_BADGE: Record<CapacityState, { label: string; tone: 'success' | 'neutral' } | null> = {
  none: null,
  below_target: null,
  target_met: { label: 'Target met', tone: 'success' },
  over_capacity: { label: 'Over capacity', tone: 'neutral' },
};

/** "Email Campaign 20,000 candidates · Social Media Campaign 3 posts"; `—` when empty. */
export function inclusionsLabel(inclusions: readonly DeliverableInclusion[] | null | undefined): string {
  if (!inclusions || inclusions.length === 0) return '—';
  return inclusions
    .map((i) => {
      const unit = i.quantity === 1 ? i.unitSingular : i.unitPlural;
      return unit ? `${i.name} ${count(i.quantity)} ${unit}` : `${i.name} ${count(i.quantity)}`;
    })
    .join(' · ');
}
